/**
 * ATL-843 — URL path-segment encoding for the typed API client.
 *
 * ## The defect this exists to prevent
 *
 * Every endpoint in `./endpoints/` addresses a row by interpolating its id into a URL path:
 *
 * ```ts
 * client.patch(`/discount-options/${id}`, …)   // WRONG — id goes out raw
 * ```
 *
 * A raw template literal writes the id into the URL **unencoded**, so any id containing a
 * URL-significant character is mangled or rejected before it can name a row. Ids of exactly that
 * shape are live in production (measured 2026-08-04, `atlas-query`):
 *
 * | id shape | rows | orgs | what an unencoded path does |
 * |---|---|---|---|
 * | `discount_options.firestore_id` = `5%`, `10%`, … | 1,987 | 299 | `%` starts a percent-escape. `/discount-options/5%` is not a valid URI, and Express's `decodeURIComponent` on the param throws → **400**, before any handler runs. |
 * | `recurring_spendings.firestore_id` = `recurringSpendings#<sha1>` | 298 | 201 | `#` is the fragment delimiter: the suffix never leaves the device. 0 of the 298 have a row at the truncation prefix, so these genuinely 404. |
 * | `spending_categories.firestore_id` with `#` | 7 | 4 | **All 7, in all 4 orgs, have a live same-org row whose `firestore_id` IS the truncation prefix.** See below. |
 *
 * ## Read the second column honestly: these are unsafe IDS, not (yet) failing writes
 *
 * The constants write path — `EditableListScreen` → `updateConstantsData` →
 * `syncEditableConstantToSupabase` (`apps/atlas/global/Firebase/firebase.ts`) — has WORKED AROUND
 * this since `dfa0b31d` (2026-01-23, v6.9.0): before a PATCH/DELETE it resolves the id to the
 * Supabase UUID via `getSupabaseIdMapping()`, and its comment names `"50%"` outright. So the raw
 * `%` id reaches the wire only on that resolver's `if (!apiId) apiId = item.id` fallback. (The
 * `saveDiscountOptions` / `deleteDiscountOption` helpers in the same file DO send the raw id, but
 * they have no production caller — only `utils/constantsDualWriteTest.js`, the dev QA harness.)
 *
 * That workaround is why this is a latent defect rather than a fleet-wide outage, and it is not a
 * reason to leave the URL wrong: it costs a `/settings` round-trip on every save, and when it
 * misses it fails silently into exactly the behaviour below.
 *
 * ## The `#` case is a live WRONG-ROW write, and it does not fail closed
 *
 * A `#` is not rejected — it is *truncated*, and what remains is a shorter, perfectly valid id.
 * For `spending_categories` that remainder names a real, different row in the same org:
 *
 * ```
 *   adminSpendingCategories1694524355558#dbe6cffe  → "Другое"          09beca36…
 *   adminSpendingCategories1694524355558           → "Аренда"          e5ebf9c8…   ← what gets written
 *   spendingCategories1779208415099#27f73d55       → "Чай / кава"      f7658c46…
 *   spendingCategories1779208415099                → "Вода"            faa26bac…   ← what gets written
 * ```
 *
 * `@Patch(':id')` and `@Delete(':id')` sit on that controller, so an un-updated bundle editing
 * "Другое" edits "Аренда", and deleting it deletes "Аренда". In spending data. Server-side
 * refusal of such ambiguous ids is ATL-848, because it protects devices this file cannot reach.
 *
 * ## The fix
 *
 * `apiPath` is a tagged template that percent-encodes every interpolated value, and nothing
 * else. Use it for EVERY path built from a value:
 *
 * ```ts
 * client.patch(apiPath`/discount-options/${id}`, …)   // → /discount-options/5%25
 * ```
 *
 * The literal parts of the template (`/discount-options/`) are emitted verbatim, so the slashes
 * that separate segments survive; only the substituted values are encoded. `scripts/check-api-path-encoding.mjs`
 * fails CI if a path-shaped template literal in this package is not tagged with it.
 *
 * ## Deployment note
 *
 * This is a CLIENT-side fix. Old bundles in the field keep sending the raw `%`/`#` until they
 * update, so the affected populations above only become addressable for a device running a
 * bundle that contains this file.
 *
 * Once an id has been truncated at a `#` the INTENDED id is unrecoverable server-side — it was
 * never transmitted. The wrong-row WRITE, however, is preventable today without any bundle:
 * refuse a PATCH/DELETE whose id is a truncation-ambiguous prefix (ATL-848). Do not read this
 * paragraph as "nothing can be done until devices update".
 */

/**
 * Thrown when a value cannot be used as a URL path segment at all — as opposed to merely
 * needing encoding.
 *
 * Failing loudly is the point. Every rejected shape below silently addresses *a different
 * resource* if it is allowed through, which is the exact defect class ATL-843 is about:
 * an empty segment collapses `/discount-options/<id>` to the collection route
 * `/discount-options`, and `.`/`..` are resolved by URL normalization before the request is
 * routed. A thrown error is visible in Sentry via the dual-write reporter; a wrong-row write
 * is not visible anywhere.
 */
export class InvalidPathSegmentError extends Error {
  constructor(
    public readonly value: string,
    public readonly reason: string,
  ) {
    super(`Cannot address a resource by id ${JSON.stringify(value)}: ${reason}`);
    this.name = 'InvalidPathSegmentError';
  }
}

/**
 * Percent-encode one value for use as a single URL path segment.
 *
 * Exported for callers that build a path outside a template literal; prefer {@link apiPath}.
 */
export function encodePathSegment(value: string | number): string {
  const raw = typeof value === 'number' ? String(value) : value;

  if (raw.length === 0 || raw.trim().length === 0) {
    throw new InvalidPathSegmentError(raw, 'the id is empty, so the URL would address the collection');
  }
  if (raw === '.' || raw === '..') {
    throw new InvalidPathSegmentError(raw, 'URL normalization resolves this segment away');
  }

  return encodeURIComponent(raw);
}

/**
 * Tagged template that percent-encodes every interpolated value as a URL path segment.
 *
 * ```ts
 * apiPath`/discount-options/${'5%'}`                       // '/discount-options/5%25'
 * apiPath`/sales/${saleId}/returns/${returnId}`            // both ids encoded
 * ```
 *
 * Values are typed `string | number` deliberately: `undefined` interpolated into a raw
 * template literal becomes the four-character id `"undefined"` and is sent to the server as
 * a real id, which is how `firestore_id IN ('undefined','null')` became a thing worth
 * querying for. Here it is a compile error.
 */
export function apiPath(
  strings: TemplateStringsArray,
  ...values: ReadonlyArray<string | number>
): string {
  let out = strings[0];
  for (let i = 0; i < values.length; i += 1) {
    out += encodePathSegment(values[i]) + strings[i + 1];
  }
  return out;
}
